import React, { useMemo } from 'react';
import {
  useIsFeatureEnabled,
  usePermissionsForCurrentUser,
  useSelectedOrgUnit,
} from '@retail/app/stores/selected-context';
import { useFetchMemberCampaigns } from '@retail/campaign/data-access';
import { CampaignFilterType, MemberCampaign } from '@retail/campaign/types';
import { getCampaignBaseColumns, getMemberCampaignColumns } from '@retail/campaign/components';
import { useCampaignTFunction } from '@retail/campaign/i18n';
import { GridColDef } from '@mui/x-data-grid';
import { useCampaignsSearchParams } from '@retail/campaign/context';
import { Action } from '@retail/auth/types';

interface ChildrenProps {
  campaigns: MemberCampaign[];
  isLoading: boolean;
  status: 'error' | 'success' | 'loading';
  campaignsTableColumns: GridColDef[];
  availableFilters: CampaignFilterType[];
  campaignActions: Action[];
}

const availableMemberCampaignFilters: CampaignFilterType[] = [
  'campaignType',
  'datePreset',
  'search',
];

interface Props {
  children: (props: ChildrenProps) => React.ReactNode;
}

export const MemberCampaignsWrapper = ({ children }: Props) => {
  const { assortmentId } = useSelectedOrgUnit();
  const { userCanUpdate, userCanDelete, userPermissions } = usePermissionsForCurrentUser();
  const localCampaignIsEnabled = useIsFeatureEnabled('local_campaigns');

  const campaignActions = localCampaignIsEnabled
    ? userPermissions.find((p) => p.resource === 'LOCAL_CAMPAIGN')?.actions ?? []
    : [];

  const t = useCampaignTFunction();

  const searchParams = useCampaignsSearchParams(availableMemberCampaignFilters);

  const campaignsTableColumns = useMemo(() => {
    const campaignBaseColumns = getCampaignBaseColumns(t);
    const memberCampaignColumns = getMemberCampaignColumns(t);

    return [
      memberCampaignColumns.enrolledColumn,
      campaignBaseColumns.campaignNameColumn,
      campaignBaseColumns.campaignTypeColumn,
      memberCampaignColumns.mandatoryColumn,
      campaignBaseColumns.itemCountColumn,
      campaignBaseColumns.descriptionColumn,
      campaignBaseColumns.validFromValidToColumn,
      campaignBaseColumns.purchasePeriodFromPurchasePeriodToColumn,
      campaignBaseColumns.lastUpdatedColumn,
    ];
  }, [t]);

  const {
    data: campaignDtos = [],
    isFetching: isLoading,
    status,
  } = useFetchMemberCampaigns({
    ...searchParams,
    assortmentId,
    suspense: false,
  });

  const campaigns = useMemo(() => {
    const canEditLocalCampaign = userCanUpdate('LOCAL_CAMPAIGN') && localCampaignIsEnabled;
    const canDeleteLocalCampaign = userCanDelete('LOCAL_CAMPAIGN') && localCampaignIsEnabled;
    const canEditCampaign = userCanUpdate('CAMPAIGN');
    const canDeleteCampaign = userCanDelete('CAMPAIGN');

    return MemberCampaign.fromDtoList({
      dtos: campaignDtos,
      canEdit: ({ campaignType }) =>
        campaignType === 'LOCAL' ? canEditLocalCampaign : canEditCampaign,
      canDelete: ({ campaignType }) =>
        campaignType === 'LOCAL' ? canDeleteLocalCampaign : canDeleteCampaign,
    });
  }, [campaignDtos, localCampaignIsEnabled, userCanDelete, userCanUpdate]);

  return children({
    campaigns,
    isLoading,
    status,
    campaignsTableColumns,
    availableFilters: availableMemberCampaignFilters,
    campaignActions,
  });
};
